import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { useRef, useState } from 'react';
import Loading from '~/pages/loading';
import { useFocusInputOnMount } from '~/lib/hooks/useFocusInputOnMount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { type RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import {
  type CarePlanResponse,
  type CreateMultipleProblemsRequestProblem,
} from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { CarePlanInterventionStatus } from '~/lib/types/carePlan/CarePlanInterventionStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import {
  useCreateCarePlanMutation,
  useCreateMultipleProblemsMutation,
} from '~/store/services/carePlan';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import {
  useGetRefCarePlanFrequenciesEnumQuery,
  useGetRefCarePlanGoalsEnumQuery,
  useGetRefCarePlanInterventionsEnumQuery,
  useGetRefCarePlanManifestationsEnumQuery,
} from '~/store/services/refCarePlanEnums';
import {
  useGetFormTreatmentAttributesQuery,
  useGetModalityTreatmentAttributesQuery,
} from '~/store/services/treatmentAttributes';
import { usePhaseStageCodesForModality } from '~/lib/hooks/useGetPhaseStagesForModality';
import { RESOLVED_FREQUENCY_CODES } from '~/lib/constants/carePlan.constants';
import {
  type CoOccurringProblemDetails,
  type EdProblemDetails,
  type ProblemDetails,
  type ProblemDetailsByCode,
  type SavedProblem,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanProblemDialogManager';
import { ProblemDetailsEdCard } from '~/components/maud/patients/profile/tabs/careplan/ProblemDetailsEdCard';
import { ProblemDetailsCoOccurringCard } from '~/components/maud/patients/profile/tabs/careplan/ProblemDetailsCoOccurringCard';

const TOP_LEVEL_ID = 'add-problem-details-modal';

const PROBLEM_TYPE_MODAL_TITLES: Record<ProblemTypeCode, string> = {
  EATING_DISORDER: 'Add ED problem',
  MEDICAL: 'Add medical problem',
  PSYCHIATRIC: 'Add co-occurring psych problem',
};

// ED goals and med/psych goals use different status sets, so the status a new goal starts in depends on the problem's type.
const DEFAULT_GOAL_STATUS_BY_PROBLEM_TYPE: Record<
  ProblemTypeCode,
  CarePlanGoalStatus
> = {
  EATING_DISORDER: CarePlanGoalStatus.NOT_STARTED,
  MEDICAL: CarePlanGoalStatus.REPORTED,
  PSYCHIATRIC: CarePlanGoalStatus.REPORTED,
};

const HEADER_INSTRUCTIONS =
  'Please provide any additional information for how the problem(s) manifest for the patient';

const CREATE_PROBLEMS_TOAST_ARGS = {
  id: 'save-problems-unexpected-error',
  icon: FAIconName.exclamationTriangleRegular,
  text: 'We encountered an unexpected error. Please try again.',
  variant: 'critical',
} as const;

const isCoOccurringType = (problemTypeCode: ProblemTypeCode) =>
  problemTypeCode === ProblemTypeCode.MEDICAL ||
  problemTypeCode === ProblemTypeCode.PSYCHIATRIC;

export type AddProblemDetailsModalProps = {
  activeCarePlan?: CarePlanResponse;
  noteExternalId?: string;
  onBack: () => void;
  onClose: () => void;
  onSubmitSuccess?: (savedProblems: SavedProblem[], carePlanId: string) => void;
  problems: RefCarePlanProblemResponse[];
  problemTypeCode: ProblemTypeCode;
  problemDetailsByCode: ProblemDetailsByCode;
  treatmentEpisodeId: string;
  updateProblemDetails: (problemCode: string, next: ProblemDetails) => void;
};

export const AddProblemDetailsModal = ({
  activeCarePlan,
  noteExternalId,
  onBack,
  onClose,
  onSubmitSuccess,
  problems,
  problemTypeCode,
  problemDetailsByCode,
  treatmentEpisodeId,
  updateProblemDetails,
}: AddProblemDetailsModalProps) => {
  const { trackEvent } = useAnalytics();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const { createToast } = useToastMessage();
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useFocusInputOnMount(
    () =>
      bodyRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        'input:not([type="hidden"]), textarea',
      ) ?? null,
  );

  const isCoOccurring = isCoOccurringType(problemTypeCode);
  const isEd = problemTypeCode === ProblemTypeCode.EATING_DISORDER;

  const { carePlanM4ModalityOptional: isModalityOptional } =
    useFlags<LDFlags>();

  const problemCodes = problems.map((problem) => problem.code);
  const {
    data: goalsForProblems,
    isError: isGoalsError,
    isLoading: isGoalsLoading,
  } = useGetRefCarePlanGoalsEnumQuery(
    { problemCodes },
    { skip: problemCodes.length === 0 },
  );

  // Modality drives which ref-interventions get seeded on ED goals.
  const { data: modalities } = useGetModalityTreatmentAttributesQuery(
    treatmentEpisodeId,
    { skip: !isEd || !!activeCarePlan },
  );
  const { data: forms } = useGetFormTreatmentAttributesQuery(
    treatmentEpisodeId,
    { skip: !isEd },
  );
  const activeModality = modalities?.find((m) => m.endDate === null);
  const activeForm = forms?.find((f) => f.endDate === null);
  const modalityCode = isEd
    ? activeCarePlan?.treatmentEpisodeModality?.modality.code ||
      activeModality?.modality.code
    : undefined;

  const {
    isLoading: isPhaseStagesLoading,
    phaseStages: phaseStagesForModality,
  } = usePhaseStageCodesForModality(modalityCode, activeForm?.form.code);

  const goalCodes = goalsForProblems?.map((g) => g.code);

  const {
    data: interventionsForGoalsAndModality,
    isError: isInterventionsError,
    isLoading: isInterventionsLoading,
  } = useGetRefCarePlanInterventionsEnumQuery(
    { goalCodes, modalityCode },
    { skip: !isEd || !goalCodes || goalCodes.length === 0 || !modalityCode },
  );

  // ED-only: fetch manifestations for every selected ED problem so we can tell, per problem, whether it has ANY selectable manifestations.
  const {
    data: manifestationsForProblems,
    isError: isManifestationsError,
    isLoading: isManifestationsLoading,
  } = useGetRefCarePlanManifestationsEnumQuery(
    { problemCodes },
    { skip: !isEd || problemCodes.length === 0 },
  );

  // Warm the frequency-options cache at the modal level so child components read from cache instead of showing their own spinner.
  const { isLoading: isFrequenciesLoading } =
    useGetRefCarePlanFrequenciesEnumQuery(undefined, { skip: !isEd });

  const problemHasAvailableManifestations = (problemCode: string) =>
    !!manifestationsForProblems?.some((m) => m.problem.code === problemCode);

  const [createMultipleProblems, { isLoading: isCreateLoading }] =
    useCreateMultipleProblemsMutation();
  const [createCarePlan, { isLoading: isCreateCarePlanLoading }] =
    useCreateCarePlanMutation();

  const missingDescriptionCodes = problems
    .filter((problem) => {
      if (!isCoOccurring) return false;
      const details = problemDetailsByCode[problem.code] as
        | CoOccurringProblemDetails
        | undefined;
      return !details?.description?.trim();
    })
    .map((problem) => problem.code);

  // A problem is "missing manifestations" only when it has selectable manifestations available AND the user hasn't picked any.
  const missingManifestationsCodes = problems
    .filter((problem) => {
      if (!isEd) return false;
      if (!problemHasAvailableManifestations(problem.code)) return false;
      const details = problemDetailsByCode[problem.code] as
        | EdProblemDetails
        | undefined;
      return Object.keys(details?.manifestationsMap ?? {}).length === 0;
    })
    .map((problem) => problem.code);

  const isSubmitting =
    isCreateLoading || isCreateCarePlanLoading || isGoalsLoading;

  const skipInterventions = isModalityOptional && !modalityCode;

  const buildEdPayload = (
    problem: RefCarePlanProblemResponse,
  ): CreateMultipleProblemsRequestProblem => {
    const details = problemDetailsByCode[problem.code] as EdProblemDetails;
    const manifestations = Object.entries(details.manifestationsMap).map(
      ([pathwayManifestationCode, { description, frequencyCode }]) => ({
        pathwayManifestationCode,
        description,
        statusCode: RESOLVED_FREQUENCY_CODES.includes(frequencyCode)
          ? ('RESOLVED' as const)
          : ('ACTIVE' as const),
        frequencyCode,
      }),
    );
    const goal = goalsForProblems?.find((g) => g.problem.code === problem.code);
    const interventions =
      skipInterventions || !interventionsForGoalsAndModality || !goal
        ? []
        : interventionsForGoalsAndModality
            .filter((intervention) =>
              intervention.goals.some((g) => g.code === goal.code),
            )
            .map((intervention) => ({
              interventionCode: intervention.code,
              statusCode: CarePlanInterventionStatus.PLANNED,
              phaseStageCode:
                intervention.phaseStages.find((phaseStage) =>
                  phaseStagesForModality.some(
                    (psfm) => psfm.code === phaseStage.code,
                  ),
                )?.code || null,
              providerTypes: intervention.providerTypes
                .filter((pt) => pt.modalityCode === modalityCode)
                .map((pt) => pt.providerType),
            }));
    return {
      problemCode: problem.code,
      description: details.description.trim() || null,
      frequencyCode: details.frequencyCode,
      manifestations,
      ...(noteExternalId ? { noteExternalId } : {}),
      goals: goal
        ? [
            {
              goalCode: goal.code,
              description: null,
              statusCode: DEFAULT_GOAL_STATUS_BY_PROBLEM_TYPE[problemTypeCode],
              interventions,
            },
          ]
        : [],
    };
  };

  const buildCoOccurringPayload = (
    problem: RefCarePlanProblemResponse,
  ): CreateMultipleProblemsRequestProblem => {
    const details = problemDetailsByCode[
      problem.code
    ] as CoOccurringProblemDetails;
    const goal = goalsForProblems?.find((g) => g.problem.code === problem.code);
    return {
      problemCode: problem.code,
      description: details.description.trim(),
      manifestations: null,
      ...(noteExternalId ? { noteExternalId } : {}),
      goals: goal
        ? [
            {
              goalCode: goal.code,
              description: null,
              statusCode: DEFAULT_GOAL_STATUS_BY_PROBLEM_TYPE[problemTypeCode],
              interventions: null,
            },
          ]
        : [],
    };
  };

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    if (isCoOccurring && missingDescriptionCodes.length > 0) return;
    if (isEd && missingManifestationsCodes.length > 0) return;

    if (isGoalsError || !goalsForProblems) {
      createToast(CREATE_PROBLEMS_TOAST_ARGS);
      return;
    }

    // Errors on the ED ref-data queries also block save.
    if (isEd && isManifestationsError) {
      createToast(CREATE_PROBLEMS_TOAST_ARGS);
      return;
    }
    if (isEd && !skipInterventions && isInterventionsError) {
      createToast(CREATE_PROBLEMS_TOAST_ARGS);
      return;
    }

    // With a modality set, ED intervention seeding needs BOTH the interventions ref-data and the phase-stages enum.
    // This covers races where state changed between render and click.
    if (
      isEd &&
      !skipInterventions &&
      (!interventionsForGoalsAndModality || phaseStagesForModality.length === 0)
    ) {
      createToast(CREATE_PROBLEMS_TOAST_ARGS);
      return;
    }

    let carePlanId = activeCarePlan?.id;
    if (!carePlanId) {
      try {
        const created = await createCarePlan({
          includeCurrent: false,
          statusCode: CarePlanStatus.ACTIVE,
          treatmentEpisodeId,
        }).unwrap();
        carePlanId = created.id;
      } catch (error) {
        console.error(
          'Failed to create care plan before batch problem save',
          error,
        );
        createToast(CREATE_PROBLEMS_TOAST_ARGS);
        return;
      }
    }

    const payload: CreateMultipleProblemsRequestProblem[] = problems.map(
      (problem) =>
        isEd ? buildEdPayload(problem) : buildCoOccurringPayload(problem),
    );

    try {
      const createProblemsResponse = await createMultipleProblems({
        carePlanId,
        problems: payload,
        treatmentEpisodeId,
      }).unwrap();
      const savedProblems: SavedProblem[] = createProblemsResponse.data.map(
        (id, index) => ({
          id,
          problem: problems[index],
        }),
      );
      onSubmitSuccess?.(savedProblems, carePlanId);
    } catch (error) {
      console.error('Failed to batch-create care plan problems', error);
      createToast(CREATE_PROBLEMS_TOAST_ARGS);
    }
  };

  return (
    <ModalV2
      footerContent={
        <ChakraHStack
          flexDirection="row-reverse"
          justifyContent="space-between"
          paddingTop="14px"
          width="100%"
        >
          <FireflyButton
            id="add-problem-details-modal-next"
            isDisabled={
              isSubmitting ||
              (isEd && isManifestationsLoading) ||
              (isEd && isFrequenciesLoading) ||
              (isEd && !!modalityCode && isPhaseStagesLoading) ||
              (isEd && missingManifestationsCodes.length > 0) ||
              (isEd &&
                !!modalityCode &&
                !!goalCodes?.length &&
                isInterventionsLoading)
            }
            isLoading={isSubmitting}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(
                CarePlanEvents.clickOnAddToCarePlanButtonInAddProblemModal,
              );
              handleSubmit();
            }}
          >
            Add to care plan
          </FireflyButton>
          <FireflyButton
            id="add-problem-details-modal-back"
            leftIcon={FAIconName.chevronLeftRegular}
            onClick={() => {
              trackEvent(CarePlanEvents.clickOnBackButtonInAddProblemModal);
              onBack();
            }}
            variant="secondary"
          >
            Back
          </FireflyButton>
        </ChakraHStack>
      }
      id={TOP_LEVEL_ID}
      isOpen
      onClose={() => {
        onClose();
        trackEvent(CarePlanEvents.clickOnCloseIconInAddProblemModal);
      }}
      size="3xl"
      title={PROBLEM_TYPE_MODAL_TITLES[problemTypeCode]}
    >
      <ChakraBox ref={bodyRef}>
        <Text color="neutral.secondary" marginBottom="18px">
          {HEADER_INSTRUCTIONS}
        </Text>
        {isEd && (isManifestationsLoading || isFrequenciesLoading) ? (
          <Loading />
        ) : (
          <ChakraFlex flexDirection="column" gap="18px">
            {problems.map((problem) =>
              problemTypeCode === ProblemTypeCode.EATING_DISORDER ? (
                <ProblemDetailsEdCard
                  details={
                    problemDetailsByCode[problem.code] as EdProblemDetails
                  }
                  key={problem.code}
                  manifestationsForED={
                    manifestationsForProblems?.filter(
                      (m) => m.problem.code === problem.code,
                    ) ?? []
                  }
                  modalId={TOP_LEVEL_ID}
                  problem={problem}
                  updateProblemDetails={updateProblemDetails}
                />
              ) : (
                <ProblemDetailsCoOccurringCard
                  details={
                    problemDetailsByCode[
                      problem.code
                    ] as CoOccurringProblemDetails
                  }
                  hasAttemptedSubmit={hasAttemptedSubmit}
                  idPrefix={TOP_LEVEL_ID}
                  key={problem.code}
                  problem={problem}
                  updateProblemDetails={updateProblemDetails}
                />
              ),
            )}
          </ChakraFlex>
        )}
      </ChakraBox>
    </ModalV2>
  );
};
